import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Twitter, 
  Facebook, 
  Instagram, 
  MessageCircle,
  CheckCircle,
  XCircle,
  Link,
  Zap
} from 'lucide-react';
import { useConnectedAccounts } from '@/hooks/useConnectedAccounts';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface ConnectedPlatformsStatusProps {
  showTitle?: boolean;
  compact?: boolean;
  className?: string;
}

export const ConnectedPlatformsStatus: React.FC<ConnectedPlatformsStatusProps> = ({
  showTitle = true,
  compact = false,
  className = ""
}) => {
  const { user } = useAuth();
  const { accounts, loading } = useConnectedAccounts();
  const navigate = useNavigate();
  const [platformHealth, setPlatformHealth] = React.useState<Record<string, boolean>>({});

  const getPlatformIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'twitter':
      case 'x':
        return <Twitter className="h-4 w-4" />;
      case 'facebook':
        return <Facebook className="h-4 w-4" />;
      case 'instagram':
        return <Instagram className="h-4 w-4" />;
      case 'threads':
        return <MessageCircle className="h-4 w-4" />;
      case 'reddit':
        return <MessageCircle className="h-4 w-4" />;
      default:
        return <Zap className="h-4 w-4" />;
    }
  };

  const getPlatformDisplayName = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'twitter':
        return 'X (Twitter)';
      case 'facebook':
        return 'Facebook';
      case 'instagram':
        return 'Instagram';
      case 'threads':
        return 'Threads';
      case 'reddit':
        return 'Reddit';
      default:
        return platform.charAt(0).toUpperCase() + platform.slice(1);
    }
  };

  // Filter to only show truly connected accounts with valid tokens
  const connectedAccounts = React.useMemo(() => {
    return accounts.filter(account => {
      // Must be active and have proper username
      if (!account.is_active || 
          !account.account_username ||
          account.account_username.trim() === '' ||
          account.account_username === 'placeholder' ||
          account.account_username.startsWith('temp_')) {
        return false;
      }

      // Check if token is expired
      if (account.token_expires_at) {
        const expiresAt = new Date(account.token_expires_at);
        const now = new Date();
        if (expiresAt <= now) {
          console.log(`❌ Token expired for ${account.platform} account:`, {
            platform: account.platform,
            username: account.account_username,
            expiresAt: expiresAt.toISOString(),
            now: now.toISOString()
          });
          return false; // Token is expired
        }
      }

      return true;
    });
  }, [accounts]);

  // Group accounts by platform and check if any are active
  const platformStatus = React.useMemo(() => {
    // Include all major platforms including Reddit
    const platforms = ['twitter', 'facebook', 'instagram', 'threads', 'reddit'];
    
    return platforms.map(platform => {
      const platformAccounts = connectedAccounts.filter(acc => 
        acc.platform.toLowerCase() === platform.toLowerCase()
      );
      
      return {
        platform,
        displayName: getPlatformDisplayName(platform),
        icon: getPlatformIcon(platform),
        isConnected: platformAccounts.length > 0,
        accountCount: platformAccounts.length,
        totalAccounts: platformAccounts.length,
        accounts: platformAccounts
      };
    });
  }, [connectedAccounts]);

  // Real-time health check for platforms showing as connected
  const checkPlatformHealth = React.useCallback(async () => {
    if (!user) return;

    const healthChecks: Record<string, boolean> = {};
    
    for (const platform of platformStatus) {
      if (!platform.isConnected) {
        healthChecks[platform.platform] = false;
        continue;
      }

      try {
        if (platform.platform === 'reddit') {
          // Real API test for Reddit with proper user ID validation
          const { data, error } = await supabase.functions.invoke('fetch-reddit-feed', {
            body: { 
              userId: user.id, 
              user_id: user.id,
              limit: 1,
              testConnection: true,
              subreddits: ['all']
            }
          });
          
          if (error || !data?.success) {
            console.log(`❌ Reddit health check failed:`, { error, data });
            healthChecks[platform.platform] = false;
          } else {
            console.log(`✅ Reddit health check passed:`, data);
            healthChecks[platform.platform] = true;
          }
        } else if (platform.platform === 'twitter') {
          // Real API test for Twitter with proper parameters
          try {
            const { data, error } = await supabase.functions.invoke('test-twitter-connection', {
              body: { userId: user.id, user_id: user.id }
            });
            
            if (error || !data?.success || !data?.isHealthy) {
              console.log(`❌ Twitter health check failed:`, { error, data });
              healthChecks[platform.platform] = false;
            } else {
              console.log(`✅ Twitter health check passed:`, data);
              healthChecks[platform.platform] = true;
            }
          } catch (testError) {
            console.log(`❌ Twitter test connection error:`, testError);
            healthChecks[platform.platform] = false;
          }
        } else if (platform.platform === 'facebook') {
          // Real API test for Facebook
          try {
            const { data, error } = await supabase.functions.invoke('fetch-facebook-feed', {
              body: { userId: user.id, user_id: user.id, testConnection: true, limit: 1 }
            });
            
            if (error || !data?.success) {
              console.log(`❌ Facebook health check failed:`, { error, data });
              healthChecks[platform.platform] = false;
            } else {
              console.log(`✅ Facebook health check passed:`, data);
              healthChecks[platform.platform] = true;
            }
          } catch (testError) {
            console.log(`❌ Facebook test connection error:`, testError);
            healthChecks[platform.platform] = false;
          }
        } else {
          // For other platforms, basic token validation
          const hasValidConnection = platform.accounts.some(acc => {
            const hasToken = acc.access_token && acc.access_token.length > 10;
            const hasValidUsername = acc.account_username && 
              !acc.account_username.startsWith('temp_') &&
              acc.account_username !== 'placeholder';
            const isNotExpired = !acc.token_expires_at || 
              new Date(acc.token_expires_at) > new Date();
            
            console.log(`🔍 ${platform.platform} basic validation:`, {
              username: acc.account_username,
              hasToken,
              hasValidUsername,
              isNotExpired,
              overall: hasToken && hasValidUsername && isNotExpired
            });
            
            return hasToken && hasValidUsername && isNotExpired;
          });
          healthChecks[platform.platform] = hasValidConnection;
        }
      } catch (error) {
        console.error(`❌ Health check failed for ${platform.platform}:`, error);
        healthChecks[platform.platform] = false;
      }
    }

    console.log('🔍 Platform health check results:', healthChecks);
    setPlatformHealth(healthChecks);
  }, [user, platformStatus]);

  // Run health checks when platforms change
  React.useEffect(() => {
    if (platformStatus.some(p => p.isConnected)) {
      checkPlatformHealth();
    }
  }, [platformStatus, checkPlatformHealth]);

  const connectedCount = platformStatus.filter(p => p.isConnected && platformHealth[p.platform] !== false).length;
  const totalPlatforms = platformStatus.length;

  if (loading) {
    return (
      <Card className={`mobile-card ${className}`}>
        <CardContent className="py-4">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-muted rounded animate-pulse" />
            <span className="text-sm text-muted-foreground">Loading platforms...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (compact) {
    return (
      <Card className={`mobile-card ${className}`}>
        <CardContent className="py-3 mobile-spacing">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
            <div className="flex items-center space-x-2">
              <CheckCircle className={`h-4 w-4 ${connectedCount > 0 ? 'text-green-500' : 'text-gray-400'}`} />
              <span className="text-sm font-medium">
                {connectedCount} of {totalPlatforms} platforms connected
              </span>
            </div>
            <div className="flex flex-wrap gap-1">
              {platformStatus.map(({ platform, icon, isConnected }) => (
                <div
                  key={platform}
                  className={`flex items-center space-x-1 px-2 py-1 rounded-md text-xs ${
                    isConnected 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {icon}
                  {isConnected && <CheckCircle className="h-3 w-3" />}
                </div>
              ))}
            </div>
            {connectedCount === 0 && (
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => navigate('/dashboard/platforms')}
                className="mobile-button"
              >
                <Link className="h-3 w-3 mr-1" />
                Connect
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`mobile-card ${className}`}>
      {showTitle && (
        <CardHeader className="pb-3">
          <CardTitle className="text-base sm:text-lg flex items-center space-x-2">
            <Link className="h-4 w-4 sm:h-5 sm:w-5" />
            <span>Connected Platforms</span>
            <Badge variant={connectedCount > 0 ? "default" : "secondary"} className="ml-auto">
              {connectedCount}/{totalPlatforms}
            </Badge>
          </CardTitle>
        </CardHeader>
      )}
      <CardContent className="mobile-spacing">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {platformStatus.map(({ platform, displayName, icon, isConnected, accountCount, accounts }) => {
            const isHealthy = platformHealth[platform] !== false;
            const showAsConnected = isConnected && isHealthy;
            
            return (
              <div
                key={platform}
                className="flex items-center justify-between p-3 border rounded-lg mobile-spacing"
              >
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-md ${showAsConnected ? 'bg-green-100' : 'bg-gray-100'}`}>
                    {icon}
                  </div>
                  <div>
                    <p className="font-medium text-sm break-words">{displayName}</p>
                    {showAsConnected && accountCount > 0 ? (
                      <p className="text-xs text-muted-foreground">
                        {accountCount} account{accountCount > 1 ? 's' : ''} connected
                      </p>
                    ) : isConnected && !isHealthy ? (
                      <p className="text-xs text-red-600">Connection issue detected</p>
                    ) : (
                      <p className="text-xs text-muted-foreground">Not connected</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {showAsConnected ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : isConnected && !isHealthy ? (
                    <XCircle className="h-5 w-5 text-red-500" />
                  ) : (
                    <XCircle className="h-5 w-5 text-gray-400" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        {connectedCount === 0 && (
          <div className="mt-4 text-center">
            <p className="text-sm text-muted-foreground mb-3">
              Connect your social media accounts to access live feeds and monitoring.
            </p>
            <Button 
              onClick={() => navigate('/dashboard/platforms')}
              className="mobile-button w-full sm:w-auto"
            >
              <Link className="h-4 w-4 mr-2" />
              Connect Platforms
            </Button>
          </div>
        )}
        
        {connectedCount > 0 && connectedCount < totalPlatforms && (
          <div className="mt-4 text-center">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/dashboard/platforms')}
              className="mobile-button w-full sm:w-auto"
            >
              <Link className="h-4 w-4 mr-2" />
              Connect More Platforms
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};